export type Heading = { id: string; text: string; level: 2 | 3 };

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function inlineFmt(s: string): string {
  return s
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-charcoal-800">$1</strong>')
    .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em class="italic">$1</em>')
    .replace(
      /`([^`]+)`/g,
      '<code class="border border-charcoal-800 bg-paper px-1.5 py-0.5 font-marketing-mono text-[13.5px] text-charcoal-800">$1</code>',
    )
    .replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" class="font-semibold text-charcoal-800 underline decoration-lime-500 decoration-2 underline-offset-4 hover:decoration-4">$1</a>',
    );
}

/**
 * Parse trusted first-party markdown (static JSON files in public/blog/) into
 * neobrutal-styled HTML, and extract the H2/H3 headings for the table of contents.
 */
export function renderArticle(md: string): { html: string; headings: Heading[] } {
  const headings: Heading[] = [];
  const lines = md.split('\n');
  const out: string[] = [];
  let inList = false;
  let listType: 'ul' | 'ol' | null = null;
  let inCode = false;

  const closeList = () => {
    if (inList && listType) {
      out.push(`</${listType}>`);
      inList = false;
      listType = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();

    if (line.startsWith('```')) {
      closeList();
      if (inCode) {
        out.push('</code></pre>');
        inCode = false;
      } else {
        out.push(
          '<pre class="my-6 overflow-x-auto border-2 border-charcoal-800 bg-charcoal-900 p-5 shadow-neobrutal-4"><code class="font-marketing-mono text-[13.5px] text-lime-400">',
        );
        inCode = true;
      }
      continue;
    }

    if (inCode) {
      out.push(line.replace(/</g, '&lt;'));
      continue;
    }

    const h2 = /^##\s+(.+)$/.exec(line);
    if (h2 && h2[1]) {
      closeList();
      const text = h2[1].trim();
      const id = slugify(text);
      headings.push({ id, text, level: 2 });
      out.push(
        `<h2 id="${id}" class="scroll-mt-28 mt-14 mb-5 font-marketing-display text-[clamp(28px,3.4vw,42px)] font-bold tracking-[-0.03em] leading-[1.05] text-charcoal-800">${text}</h2>`,
      );
      continue;
    }

    const h3 = /^###\s+(.+)$/.exec(line);
    if (h3 && h3[1]) {
      closeList();
      const text = h3[1].trim();
      const id = slugify(text);
      headings.push({ id, text, level: 3 });
      out.push(
        `<h3 id="${id}" class="scroll-mt-28 mt-10 mb-3 font-marketing-display text-[22px] font-bold tracking-[-0.02em] leading-[1.2] text-charcoal-800 md:text-[24px]">${text}</h3>`,
      );
      continue;
    }

    const bq = /^>\s?(.*)$/.exec(line);
    if (bq) {
      closeList();
      out.push(
        `<blockquote class="my-7 border-l-[3px] border-lime-500 bg-paper px-5 py-4 font-marketing-body text-[17px] italic leading-relaxed text-charcoal-700">${inlineFmt(bq[1] ?? '')}</blockquote>`,
      );
      continue;
    }

    const ul = /^-\s+(.+)$/.exec(line);
    if (ul && ul[1]) {
      if (!inList || listType !== 'ul') {
        closeList();
        out.push('<ul class="my-5 space-y-2.5">');
        inList = true;
        listType = 'ul';
      }
      out.push(
        `<li class="flex items-start gap-3 font-marketing-body text-[16px] leading-relaxed text-charcoal-700"><span class="mt-[10px] h-1.5 w-1.5 flex-shrink-0 bg-lime-500"></span><span>${inlineFmt(ul[1])}</span></li>`,
      );
      continue;
    }

    const ol = /^(\d+)\.\s+(.+)$/.exec(line);
    if (ol && ol[1] && ol[2]) {
      if (!inList || listType !== 'ol') {
        closeList();
        out.push('<ol class="my-5 space-y-3">');
        inList = true;
        listType = 'ol';
      }
      out.push(
        `<li class="flex items-start gap-3.5 font-marketing-body text-[16px] leading-relaxed text-charcoal-700"><span class="mt-[2px] flex h-6 w-6 flex-shrink-0 items-center justify-center border-2 border-charcoal-800 bg-lime-500 font-marketing-display text-[11px] font-bold text-charcoal-800">${ol[1]}</span><span>${inlineFmt(ol[2])}</span></li>`,
      );
      continue;
    }

    if (line.trim() === '') {
      closeList();
      continue;
    }

    closeList();
    out.push(
      `<p class="my-4 font-marketing-body text-[16.5px] leading-[1.7] text-charcoal-700 md:text-[17px]">${inlineFmt(line)}</p>`,
    );
  }

  closeList();
  if (inCode) out.push('</code></pre>');

  return { html: out.join('\n'), headings };
}
